/**
 * Console Interceptor Service
 * 
 * Automatically intercepts all console.log, console.warn, and console.error calls
 * and forwards them to CloudWatch via cloudWatchLogger.
 * 
 * This allows existing console statements throughout the codebase to be automatically
 * logged to CloudWatch without any code changes.
 * 
 * Features:
 * - Preserves original console behavior (logs still appear in browser DevTools)
 * - Only active in production (development uses normal console)
 * - Extracts Error objects from console.error calls
 * - Formats multiple arguments into readable messages
 */

import { cloudWatchLogger } from '../services/CloudWatchLoggerService';

class ConsoleInterceptor {
  private originalConsole = {
    log: console.log,
    info: console.info,
    warn: console.warn,
    error: console.error,
    debug: console.debug
  };

  private isInitialized = false;
  private isDevelopment = process.env.NODE_ENV !== 'production';
  private isProcessingLog = false; // Recursion guard

  /**
   * Initialize console interception
   * Only activates in production mode
   */
  initialize(): void {
    if (this.isInitialized) {
      console.warn('[ConsoleInterceptor] Already initialized');
      return;
    }

    if (this.isDevelopment) {
      console.log('[ConsoleInterceptor] Development mode - interception disabled');
      return;
    }

    try {
      // Override console.log → CloudWatch INFO
      console.log = (...args: any[]) => {
        this.originalConsole.log.apply(console, args);
        this.forwardToCloudWatch('info', args);
      };

      // Override console.info → CloudWatch INFO
      console.info = (...args: any[]) => {
        this.originalConsole.info.apply(console, args);
        this.forwardToCloudWatch('info', args);
      };

      // Override console.warn → CloudWatch WARN
      console.warn = (...args: any[]) => {
        this.originalConsole.warn.apply(console, args);
        this.forwardToCloudWatch('warn', args);
      };

      // Override console.error → CloudWatch ERROR
      console.error = (...args: any[]) => {
        this.originalConsole.error.apply(console, args);
        this.forwardToCloudWatch('error', args);
      };

      // Override console.debug → CloudWatch INFO (with [DEBUG] prefix)
      console.debug = (...args: any[]) => {
        this.originalConsole.debug.apply(console, args);
        this.forwardToCloudWatch('debug', args);
      };

      this.isInitialized = true;
      this.originalConsole.log('[ConsoleInterceptor] Console interception initialized');
    } catch (error) {
      this.originalConsole.error('[ConsoleInterceptor] Failed to initialize:', error);
    }
  }

  /**
   * Forward console call to CloudWatch logger
   */
  private forwardToCloudWatch(level: 'info' | 'warn' | 'error' | 'debug', args: any[]): void {
    // Guard against infinite loops - if we're already processing a log, skip
    if (this.isProcessingLog) {
      return;
    }

    try {
      this.isProcessingLog = true;
      
      // Extract message and error from arguments
      const { message, error } = this.parseConsoleArguments(args);

      // Forward to appropriate CloudWatch logger method
      switch (level) {
        case 'debug':
          // Forward debug logs as INFO with [DEBUG] prefix for visibility
          cloudWatchLogger.info(`[DEBUG] ${message}`);
          break;
        case 'info':
          cloudWatchLogger.info(message);
          break;
        case 'warn':
          cloudWatchLogger.warn(message);
          break;
        case 'error':
          cloudWatchLogger.error(message, error);
          break;
      }
    } catch (error) {
      // Silently fail to avoid breaking the application
      // Use original console to log the issue
      this.originalConsole.error('[ConsoleInterceptor] Failed to forward log:', error);
    } finally {
      this.isProcessingLog = false;
    }
  }

  /**
   * Parse console arguments into message and optional error
   */
  private parseConsoleArguments(args: any[]): { message: string; error?: Error } {
    if (args.length === 0) {
      return { message: '' };
    }

    let error: Error | undefined;
    const messageParts: string[] = [];

    for (const arg of args) {
      if (arg instanceof Error) {
        error = arg;
        messageParts.push(arg.message);
      } else if (typeof arg === 'object') {
        try {
          // For objects, use JSON.stringify but limit size
          const jsonStr = JSON.stringify(arg, null, 0);
          if (jsonStr.length > 500) {
            messageParts.push(jsonStr.substring(0, 500) + '... (truncated)');
          } else {
            messageParts.push(jsonStr);
          }
        } catch (e) {
          messageParts.push('[Object]');
        }
      } else {
        messageParts.push(String(arg));
      }
    }

    return {
      message: messageParts.join(' '),
      error
    };
  }

  /**
   * Restore original console methods (useful for testing or debugging)
   */
  restore(): void {
    if (!this.isInitialized) {
      return;
    }

    console.log = this.originalConsole.log;
    console.info = this.originalConsole.info;
    console.warn = this.originalConsole.warn;
    console.error = this.originalConsole.error;
    console.debug = this.originalConsole.debug;

    this.isInitialized = false;
    this.originalConsole.log('[ConsoleInterceptor] Console methods restored');
  }

  /**
   * Get interceptor status
   */
  getStatus(): {
    isInitialized: boolean;
    isDevelopment: boolean;
  } {
    return {
      isInitialized: this.isInitialized,
      isDevelopment: this.isDevelopment
    };
  }
}

// Export singleton instance
export const consoleInterceptor = new ConsoleInterceptor();

export default consoleInterceptor;
